import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { MyCommunitiesDocument } from 'generated/resources/graphql'

import BoxList from 'modules/core/components/BoxList'
import InlineSearch from 'modules/core/components/InlineSearch'
import { useEffect } from 'modules/core/hooks/useEffect'
import { InfiniteLoader, usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'

import GroupsList from './GroupList'

interface Props {
  selectedGroup: CommunitiesDropdown | null | undefined
  onSelect: React.Dispatch<CommunitiesDropdown>
  createGroup: () => void
  joinGroup: () => void
  excludeCelebrationGroups?: boolean
  canCreateGroup?: boolean
}

const GroupSelector = ({
  selectedGroup,
  createGroup,
  onSelect,
  joinGroup,
  excludeCelebrationGroups,
  canCreateGroup,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch('', { delay: true })
  const [trackedSearchEvent, setTrackedSearchEvent] = React.useState(false)

  const { nodes: communitiesNodes, controls } = usePaginatedQuery(MyCommunitiesDocument, {
    variables: { search: debouncedSearch, first: 100 },
    extractConnection: (data) =>
      data.posts?.groupsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
  })

  useEffect(() => {
    if (search.length && !trackedSearchEvent) {
      setTrackedSearchEvent(true)
    }
  }, [search])

  const groupsDropdown = (): CommunitiesDropdown[] => {
    return communitiesNodes.map((group) => ({
      title: group.title ?? '',
      strategy: 'group',
      value: group.id,
    }))
  }

  const dropdownOptions = (): CommunitiesDropdown[] => {
    const all_communities: CommunitiesDropdown[] = [
      { title: i18n.t('posts.all_communities.title'), strategy: 'all' },
    ]
    const types: CommunitiesDropdown[] = [
      { title: i18n.t('posts.kudos.title'), strategy: 'type', value: 'Kudo' },
    ]

    if (!excludeCelebrationGroups) {
      types.unshift(
        { title: i18n.t('posts.birthdays.title'), strategy: 'type', value: 'Birthday' },
        {
          title: i18n.t('posts.new_joiners.title'),
          strategy: 'type',
          value: 'FirstDay',
        },
        {
          title: i18n.t('posts.workiversaries.title'),
          strategy: 'type',
          value: 'Workiversary',
        }
      )
    }

    const groups = groupsDropdown()

    return [...all_communities, ...groups, ...types]
  }

  const filteredOptions = dropdownOptions().filter(
    (option: CommunitiesDropdown) => !search || matchText(search, option.title)
  )

  return (
    <Box gap="s16">
      <BoxList>
        <InlineSearch onSearch={onSearch} search={search} />
        <Box maxHeight="s240" overflowY="auto" data-scroll-container>
          <InfiniteLoader controls={controls}>
            <GroupsList
              selectedGroup={selectedGroup}
              options={filteredOptions}
              onSelect={onSelect}
            />
          </InfiniteLoader>
        </Box>
      </BoxList>
      <Box flexDirection="row" justifyContent="flexStart" gap="s8">
        {canCreateGroup && (
          <Button
            type="secondary"
            onClick={() => {
              createGroup()
            }}
            iconName="add"
          >
            {i18n.t('dashboard.groups.create_group')}
          </Button>
        )}
        <Button
          type="secondary"
          onClick={() => {
            joinGroup()
          }}
          iconName="search"
        />
      </Box>
    </Box>
  )
}

export default GroupSelector
